import React, { useEffect, useState } from "react";
import { ArrowRightIcon } from "../Token";
import { InductionSection } from "../Types";

import SufiAssistant from "../../../../assets/images/sufi.png";
import { baseUrl } from "../../../../hooks/useFetch";
import MyDiv from "./InductionExperience.style"

interface WelcomeScreenProps {
  employeeName?: string;
  companyName: string;
  branchName?: string;
  departmentName?: string;
  designationName?: string;
  joiningDate?: string;
  reportingManager?: string;
  buddyName?: string;
  employeeCode?: string;
  sections?: InductionSection[];
  resuming?: boolean;
  onStart: () => void;
}

export function WelcomeScreen({
  employeeName = "Team Member",
  companyName,
  branchName = "Main Branch",
  departmentName = "General",
  designationName = "Team Member",
  joiningDate = "Day One · Induction",
  reportingManager = "Assigned Lead",
  buddyName = "HR Partner",
  employeeCode,
  sections,
  resuming,
  onStart,
}: WelcomeScreenProps) {
  const [probedDurations, setProbedDurations] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!sections) return;
    sections.forEach((item) => {
      if (!item.videoUrl || item.videoDurationSeconds) return;
      try {
        const video = document.createElement("video");
        video.preload = "metadata";
        let fullUrl = item.videoUrl;
        if (!/^(https?:\/\/|blob:)/i.test(fullUrl)) {
          const cleanPath = fullUrl.replace(/^\/?(api\/)?/, "");
          const base = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
          fullUrl = `${base}${cleanPath}`;
        }
        video.src = fullUrl;
        video.onloadedmetadata = () => {
          if (video.duration && !Number.isNaN(video.duration) && video.duration > 0) {
            setProbedDurations((previous) => ({
              ...previous,
              [item.key]: Math.round(video.duration),
            }));
          }
        };
      } catch {
        // Duration probing is optional and must never block induction.
      }
    });
  }, [sections]);

  const chapters = (sections || []).map((item, idx) => {
    const seconds = probedDurations[item.key] || item.videoDurationSeconds;
    let duration = item.videoDurationFormatted;
    if (!duration && seconds && seconds > 0) {
      const minutes = Math.floor(seconds / 60);
      const remainder = seconds % 60;
      duration = minutes
        ? `${minutes}m ${remainder.toString().padStart(2, "0")}s`
        : `${remainder}s`;
    }
    const hasVideo = !!(item.hasVideo || item.videoUrl);
    const hasDuration = !!(seconds && seconds > 0);
    const displayDuration = hasVideo && (hasDuration || (duration && duration !== "0 min"))
      ? duration || `${Math.max(1, Math.ceil((seconds || 0) / 60))} min`
      : "Narrated chapter";
    return {
      id: item.key,
      label: item.label || item.title || `Chapter ${idx + 1}`,
      displayDuration,
      hasVideo: hasVideo && displayDuration !== "Narrated chapter",
    };
  });

  const joiningDetails = [
    { label: "Joining date", value: joiningDate },
    { label: "Department", value: departmentName },
    { label: "Designation", value: designationName },
    { label: "Reporting manager", value: reportingManager },
    { label: "Team buddy", value: buddyName },
    { label: "Employee code", value: employeeCode || "To be assigned" },
  ].filter((item) => !!item.value);

  return (
    <MyDiv>
    <main className="induction-welcome">
      <div className="welcome-ambient welcome-ambient-one" />
      <div className="welcome-ambient welcome-ambient-two" />
      <header className="welcome-header">
        <div className="scene-brand">
          <span className="scene-brand-mark">T</span>
          <span className="scene-brand-name">{companyName || "TEK Inspirations LLC"}</span>
          <span className="scene-brand-divider" />
          <span className="scene-brand-context">PEOPLE · CULTURE · GROWTH</span>
        </div>
        <span className="welcome-header-note">EMPLOYEE INDUCTION EXPERIENCE</span>
      </header>

      <div className="welcome-main">
        <div className="welcome-copy">
          <div className="welcome-kicker">
            <span className="welcome-kicker-dot" />
            {resuming ? "WELCOME BACK TO YOUR JOURNEY" : "A NEW CHAPTER BEGINS"}
          </div>
          <p className="welcome-greeting">Welcome aboard, {employeeName}.</p>
          <h1>
            Your journey at
            <span>{companyName}</span>
            starts here.
          </h1>
          <p className="welcome-description">
            Every great journey starts with knowing the people, purpose, and possibilities around you.
            I'm Sufi, and I'll personally guide you through your new workplace.
          </p>

          <div className="welcome-meta">
            <div><strong>{chapters.length || 0}</strong><span>CHAPTERS</span></div>
            <i />
            <div><strong>{chapters.filter((chapter) => chapter.hasVideo).length}</strong><span>VIDEOS</span></div>
            <i />
            <div><strong>AI</strong><span>VOICE GUIDE</span></div>
          </div>

          <button type="button" className="welcome-start-button" onClick={onStart}>
            {resuming ? "Resume your induction" : "Begin your induction"}
            <span><ArrowRightIcon boxSize={5} /></span>
          </button>

          <div className="welcome-chapters">
            <div className="welcome-chapters-heading">
              <span>YOUR INDUCTION JOURNEY</span>
              <span>{String(chapters.length).padStart(2, "0")} CHAPTERS</span>
            </div>
            <div className="welcome-chapter-list">
              {chapters.map((chapter, chapterIndex) => (
                <div className="welcome-chapter" key={`${chapter.id}-${chapterIndex}`}>
                  <span className="welcome-chapter-number">{String(chapterIndex + 1).padStart(2, "0")}</span>
                  <span className="welcome-chapter-name">{chapter.label}</span>
                  <span className="welcome-chapter-duration">{chapter.hasVideo ? chapter.displayDuration : "VOICE GUIDED"}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="welcome-guide-stage">
          <div className="welcome-guide-orbit welcome-guide-orbit-a" />
          <div className="welcome-guide-orbit welcome-guide-orbit-b" />
          <div className="welcome-guide-glow" />
          <div className="welcome-guide-image">
            <img src={SufiAssistant} alt="Sufi, your AI induction guide" />
          </div>
          <div className="welcome-guide-message">
            <div className="welcome-guide-message-top">
              <div><strong>Sufi</strong><span>YOUR AI HR GUIDE</span></div>
              <span className="welcome-ready"><i /> READY TO BEGIN</span>
            </div>
            <p>
              Hello {employeeName}. I'm excited to welcome you to {companyName}.
              Let's explore your new workplace together.
            </p>
            <div className="guide-waveform">
              {Array.from({ length: 27 }).map((_, i) => (
                <i key={i} style={{ "--wave-delay": `${i * 35}ms` } as React.CSSProperties & { "--wave-delay": string }} />
              ))}
            </div>
          </div>
          <div className="welcome-employee-note">
            <span>YOUR FIRST DAY</span>
            <strong>{branchName}</strong>
            <small>{departmentName} · {designationName}</small>
            {joiningDetails.length > 0 && (
              <div className="welcome-employee-details">
                {joiningDetails.slice(0, 4).map((detail) => (
                  <div key={detail.label}><span>{detail.label}</span><strong>{detail.value}</strong></div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <footer className="welcome-footer">
        <span>© {new Date().getFullYear()} {companyName}</span>
        <span>YOUR PEOPLE. YOUR PURPOSE. YOUR NEXT CHAPTER.</span>
        {employeeCode && <span>EMPLOYEE ID · {employeeCode}</span>}
      </footer>
    </main>
    </MyDiv>
  );
}
